import React, { FC, useState, useCallback, useEffect, useRef } from 'react'
import { useReactiveVar } from '@apollo/client'
import { Button, Modal } from '@wmgtech/legato'
import { useParams, useBlocker } from 'react-router-dom'

import { cn } from 'shared/utils'
import { ITag, IProject } from 'shared/interfaces'
import { Tag } from 'shared/__gql-types'
import { TRouterParams } from 'shared/types'

import { ConditionRender, Search, SearchItem, Tags } from 'components'

import { EnvContext } from 'modulesCommon/EnvProvider'

import { setError } from 'modules/Feed/utils'
import { galleryUrlsVar } from 'modules/Feed/store'
import { MAX_TITLE_LENGTH } from 'modules/Feed/consts'

import { FeedEditor, FeedGallery, useFeedUploader } from '..'

import { FeedFormProps } from './FeedForm.props'
import './FeedForm.scss'

const FeedForm: FC<FeedFormProps> = ({
  handleCloseForm,
  feedHandler,
  tagsList,
  isCreateFeed = false,
  initialValue = '',
  initialTitle = '',
  initialGallery = [],
  initialTagsValue = [],
  searchItems,
  searchResult = {},
  isUpdateFeed = false,
  dataTestPrefix,
  isEditorLoading = false
}) => {
  const [editorValue, setEditorValue] = useState<string>('')
  const [titleValue, setTitleValue] = useState<string>(initialTitle)
  const [tagsValue, setTagsValue] = useState<Tag[]>(initialTagsValue)
  const [isEditorEmpty, setEditorError] = useState<boolean>(true)
  const [isTagsEmpty, setTagsError] = useState<boolean>(true)
  const [isTitleEmpty, setTitleError] = useState<boolean>(true)
  const [isTitleLong, setTitleLong] = useState<boolean>(false)
  const [isEditorTouched, setEditorTouched] = useState<boolean>(false)
  const [isTagsTouched, setTagsTouched] = useState<boolean>(false)
  const [isTitleTouched, setTitleTouched] = useState<boolean>(false)
  const [addStoryIsLoading, setAddStoryIsLoading] = useState<boolean>(false)
  const [activeProject, setactiveProject] = useState<IProject>()
  const [isInitialValueChanged, setValueChanged] = useState<boolean>(false)
  const [confirmSave, showConfirmSave] = useState<boolean>(false)

  const { FeedUploader } = useFeedUploader({ initialGallery })

  const galleryUrls = useReactiveVar(galleryUrlsVar)

  const { id } = useParams<TRouterParams>()

  const navigation = useRef<(() => void) | null>(null)

  const handleEditorValue = useCallback((value, delta, source) => {
    if (source === 'user') setEditorValue(value)
    if (value === '<p><br></p>') setEditorValue('')

    setEditorTouched(true)
  }, [])

  const handleTitleValue = useCallback(({ target: { value } }) => {
    setTitleValue(value)
    setTitleTouched(true)
    setTitleLong(value.length > MAX_TITLE_LENGTH)
  }, [])

  const handleTagsValue = useCallback((value) => {
    const updatedValue = value.map(({ id }: ITag) => ({ id }))

    setTagsValue(updatedValue)
  }, [])

  const cancelSave = useCallback(() => {
    showConfirmSave(false)
    handleCloseForm()
    galleryUrlsVar([])

    if (typeof navigation.current === 'function') {
      navigation.current()
    }
  }, [handleCloseForm])

  const closeDialog = useCallback(() => {
    showConfirmSave(false)
    navigation.current = null
  }, [])

  const handleClose = useCallback(() => {
    if (isInitialValueChanged || titleValue !== initialTitle) {
      showConfirmSave(true)
    } else {
      handleCloseForm()
      galleryUrlsVar([])
    }
  }, [isInitialValueChanged, titleValue, initialTitle, handleCloseForm])

  const submitHandler = useCallback(
    (e) => {
      e.preventDefault()

      const campaign_id = id ? id : activeProject?.id
      const hasTitleLongValue = titleValue.length > MAX_TITLE_LENGTH

      if (titleValue && editorValue && !!tagsValue.length && !hasTitleLongValue) {
        setAddStoryIsLoading(true)
      }

      setError(editorValue, setEditorError)
      setError(titleValue, setTitleError)
      setError(tagsValue, setTagsError)
      setTitleLong(hasTitleLongValue)

      if (isEditorEmpty) setEditorTouched(true)
      if (isTitleEmpty || isTitleLong) setTitleTouched(true)
      if (isTagsEmpty) setTagsTouched(true)

      if (editorValue && tagsValue.length && titleValue && !hasTitleLongValue) {
        feedHandler({
          body: editorValue,
          title: titleValue,
          tags: tagsValue,
          gallery: galleryUrls,
          ...(!isUpdateFeed && campaign_id && { campaign_id: +campaign_id })
        }).then(() => {
          handleCloseForm()
          galleryUrlsVar([])
          setAddStoryIsLoading(false)
        })
      }
    },
    [
      id,
      activeProject?.id,
      titleValue,
      editorValue,
      tagsValue,
      isEditorEmpty,
      isTitleEmpty,
      isTitleLong,
      isTagsEmpty,
      feedHandler,
      galleryUrls,
      isUpdateFeed,
      handleCloseForm
    ]
  )

  const removeGalleryItem = useCallback(
    (galleryItem) => {
      galleryUrlsVar(galleryUrls.filter((url) => galleryItem !== url))
    },
    [galleryUrls]
  )

  useEffect(() => {
    if (initialValue.length) setEditorValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    if (isEditorTouched) setError(editorValue, setEditorError)
    if (isTagsTouched) setError(tagsValue, setTagsError)
    if (isTitleTouched) setError(titleValue, setTitleError)
  }, [isEditorTouched, isTagsTouched, isTitleTouched, editorValue, tagsValue, titleValue])

  /**  TODO: Route v6 need update
  *  https://reactrouter.com/en/main/upgrading/v5#prompt-is-not-currently-supported
  */

  const isBlocked = isInitialValueChanged || titleValue !== initialTitle

  const blocker = useBlocker(isBlocked)

  useEffect(
    () => {
      if (blocker.state === 'blocked' && !isBlocked) {
        blocker.reset()
      }
      if (blocker.state === 'blocked' && isBlocked) {
        showConfirmSave(true)
        navigation.current = blocker.proceed
      }
    },
    [blocker, isBlocked]
  )

  return (
    <>
      <form
        onSubmit={submitHandler}
        className={cn('feed-form', {
          'feed-form--create': isCreateFeed
        })}
      >
        <div className="feed-form__content">
          {activeProject && (
            <SearchItem
              className="feed-form__search-item"
              title={activeProject.name}
              subtitle={activeProject.artist.name}
              spotifyId={activeProject.artist.spotify_id}
              facebookId={activeProject.artist.facebook_id}
              isSpaceRole={activeProject.isSpaceRole}
              zone={activeProject.zone}
            />
          )}
          {!id && !activeProject && searchItems && (
            <div className="feed-form__search">
              <Search
                searchItems={searchItems}
                onChooseItem={setactiveProject}
                searchResult={searchResult}
                placeHolder="Type Project"
                searchBoxIconName="magnifying-glass"

              />
            </div>
          )}
          <div className="feed-form__title-box">
            <input
              id="feed-form__title"
              className="feed-form__title-field"
              type="text"
              disabled={!id && !activeProject && !initialValue.length}
              placeholder="Add Title"
              autoComplete={'off'}
              value={titleValue}
              onChange={handleTitleValue}
              data-test={`${dataTestPrefix}-title`}
            />
            {isTitleEmpty && isTitleTouched && <p className="feed-form__error">Title can't be blank</p>}
            {isTitleLong && isTitleTouched && (
              <p className="feed-form__error" data-test={`${dataTestPrefix}-title_error`}>
                Title is limited to {MAX_TITLE_LENGTH} characters
              </p>
            )}
          </div>
          <div className="feed-form__editor-wrapper">
            <FeedEditor
              value={editorValue}
              setValue={handleEditorValue}
              isDisabled={(!id && !activeProject && !initialValue.length) || isEditorLoading}
              setValueChanged={setValueChanged}
              dataTest={`${dataTestPrefix}-body`}
              placeholder="Type or paste in your story..."
            />
            {isEditorEmpty && isEditorTouched && (
              <p className="feed-form__error" data-test={`${dataTestPrefix}-body_error`}>
                Body can't be blank
              </p>
            )}
          </div>
          <div className="feed-form__tag-hint">
            <i className="fa-solid fa-circle-info" />
            <p>
              What makes a great story? Read our{' '}
              <a
                className="feed-form__guide-link"
                href="https://wmg.sharepoint.com/sites/Storybox/SitePages/Create%20a%20Story.aspx"
                target="_blank"
                rel="noreferrer"
              >
                Storybox Guide
              </a>
            </p>
          </div>
          {FeedUploader()}
          <ConditionRender
            condition={!!galleryUrls.length}
            component={
              <FeedGallery
                imagePaths={galleryUrls}
                isEditing
                removeItem={removeGalleryItem}
                dataTestPrefix={`${dataTestPrefix}-gallery`}
              />
            }
          />
          {isCreateFeed && (
            <div className="feed-form__tag-hint feed-form__tag-hint--center">
              <span className="feed-form__tag-icon fa-solid fa-tags" />
              <p>Choose tags that categorize your story. (Tags do not notify your desired target audience.)</p>
            </div>
          )}
          <div className="feed-form-tags">
            <div className="feed-form-tags__box">
              <Tags
                theme="light"
                tagsList={tagsList}
                onTouch={setTagsTouched}
                onSelect={handleTagsValue}
                initialTagsValue={initialTagsValue}
                dataTest={`${dataTestPrefix}-tags`}
              />
            </div>
            {isTagsEmpty && isTagsTouched && (
              <p className="feed-form__error" data-test={`${dataTestPrefix}-tags_error`}>
                You should choose at least one tag
              </p>
            )}
          </div>
        </div>
        <div className="feed-form__button-group">
          <Button
            className="feed-form__reset-button"
            colorType="secondary-black"
            containerStyle="outline"
            label="Cancel"
            onClick={handleClose}
            size="sm"
            data-test={`${dataTestPrefix}-btn_cancel`}
          />
          <EnvContext.Consumer>
            {({ primaryColor }) => (
              <Button
                className="feed-form__add-button"
                colorType={primaryColor}
                containerStyle="solid"
                secondaryIcon="circle-plus"
                label={isCreateFeed ? 'Add Story' : 'Save Story'}
                htmlType="submit"
                size="sm"
                disabled={addStoryIsLoading}
                data-test={`${dataTestPrefix}-btn_save`}
              />
            )}
          </EnvContext.Consumer>
        </div>
      </form>
      <Modal
        footer={
          <div className="feed-form__modal-footer">
            <Button colorType="secondary-black" containerStyle="outline" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button colorType="secondary-black" onClick={() => cancelSave()}>
              OK
            </Button>
          </div>
        }
        icon=""
        onClose={() => showConfirmSave(false)}
        size="md"
        title=""
        isOpen={confirmSave}
        shouldCloseOnOverlayClick={false}
      >
        <p className="feed-form__modal-description">
          You have unsaved changes! If you leave, your changes will be lost.
        </p>
      </Modal>
    </>
  )
}

export default FeedForm
